import { Star } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { useInView } from "../hooks/useInView";
import { useEffect, useRef, useState } from "react";

const TYPING_SPEED_MS = 22; // per character

const TypewriterText = ({ text, start, delay = 0 }) => {
  const [displayed, setDisplayed] = useState("");
  const intervalRef = useRef(null);
  const indexRef = useRef(0);

  useEffect(() => {
    setDisplayed("");
    indexRef.current = 0;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!start || !text) return;

    const startTimer = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        indexRef.current += 1;
        setDisplayed(text.slice(0, indexRef.current));
        if (indexRef.current >= text.length) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, TYPING_SPEED_MS);
    }, delay);

    return () => {
      clearTimeout(startTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [text, start, delay]);

  const isDone = displayed.length >= (text ? text.length : 0);

  return (
    <span data-testid="testimonial-typewriter">
      "{displayed}"
      {!isDone && (
        <span
          aria-hidden="true"
          className="inline-block w-[2px] h-[1em] align-[-2px] ml-0.5 bg-[#1a7a8a] animate-pulse"
        />
      )}
    </span>
  );
};

/** Each testimonial card observes itself and starts typing when it enters the viewport */
const TestimonialCard = ({ testimonial, text, name, location, service, langKey, idx }) => {
  const [cardRef, cardInView] = useInView(0.3);

  return (
    <Card
      ref={cardRef}
      className={`anim-card anim-fade-up border-0 shadow-lg hover:shadow-2xl ${cardInView ? 'is-in' : ''}`}
      style={{ animationDelay: `${idx * 0.05}s` }}
    >
      <CardContent className="p-6">
        <div className="flex mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star key={`star-${testimonial.id}-${i}`} className="text-[#1a7a8a] fill-[#1a7a8a]" size={20} />
          ))}
        </div>
        <p className="text-gray-700 mb-4 italic min-h-[7.5rem]">
          <TypewriterText
            key={`${testimonial.id}-${langKey}`}
            text={text}
            start={cardInView}
            delay={200}
          />
        </p>
        <div className="border-t pt-4">
          <div className="font-bold text-gray-900">{name}</div>
          <div className="text-sm text-gray-600">
            {testimonial.location || location}
          </div>
          <div className="text-sm text-[#1a7a8a] mt-1">{service}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const Testimonials = () => {
  const { t, i18n } = useTranslation();
  const [headerRef, headerInView] = useInView(0.1);

  const testimonialsData = [
    { id: 1, location: "Amsterdam", rating: 5 },
    { id: 2, rating: 5 },
    { id: 3, rating: 5 },
    { id: 4, location: "Amsterdam", rating: 5 }
  ];

  return (
    <section id="testimonials" ref={headerRef} className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={`anim-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 ${headerInView ? 'is-in' : ''}`}>
            {t('testimonials.title')}
          </h2>
          <div className="flex justify-center mb-4">
            <div className={`anim-heading-underline ${headerInView ? 'is-in' : ''}`} />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {testimonialsData.map((testimonial, idx) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              idx={idx}
              langKey={i18n.language}
              text={t(`testimonial.${testimonial.id}.text`)}
              name={t(`testimonial.${testimonial.id}.name`)}
              location={t(`testimonial.${testimonial.id}.location`)}
              service={t(`testimonial.${testimonial.id}.service`)}
            />
          ))}
        </div>

        {/* Google Reviews Button */}
        <div className="flex justify-center mt-12">
          <a
            href="https://share.google/mVxJzSAyF2oeef2II"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button 
              className="bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-6 text-lg font-semibold flex items-center gap-3"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('testimonials.googleButton')}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
